import React from 'react';
import { useGetMicrostoreItemsQuery, MicrostoreItemType, GetMicrostoreItemsQuery } from 'data/__generated__';
import { Option, TotemSelect } from 'components/TotemSelect';

export type MicrostoreItemSelected = GetMicrostoreItemsQuery['microstoreItems']['items'][number];

interface MicrostoreItemSelectProps {
    dataTest?: string;
    label: string;
    placeholder: string;
    value: string | null;
    onChange: (item: MicrostoreItemSelected | null) => void;
    isClearable?: boolean;
    UsedInTemplates?: boolean;
}

export const MicrostoreItemSelect: React.FC<MicrostoreItemSelectProps> = ({
    value,
    onChange,
    dataTest,
    label,
    placeholder,
    isClearable = false,
    UsedInTemplates = false,
}) => {
    const [selectedItem, setSelectedItem] = React.useState<MicrostoreItemSelected | null>(null);

    const { data: microstoreItemsData, refetch } = useGetMicrostoreItemsQuery({
        variables: {
            offset: 0,
            limit: 30,
            sortBy: 'name',
            sortOrder: 'asc',
            filters: {
                searchValue: value || undefined,
            },
        },
        fetchPolicy: 'cache-and-network',
    });

    const createItemLabel = (item: { type: MicrostoreItemType; name: string; locationCount?: number }) => {
        const typeLabel = item.type === MicrostoreItemType.Product ? 'Produit' : 'Gamme';
        const templateCountLabel =
            item.locationCount !== undefined
                ? ` - ${item.locationCount} emplacement${item.locationCount > 1 ? 's' : ''}`
                : '';
        return `${item.name} (${typeLabel}${templateCountLabel})`;
    };

    const defaultOptions = React.useMemo(() => {
        const queryItems = microstoreItemsData?.microstoreItems?.items || [];
        const allItems = [...queryItems];

        if (selectedItem && !queryItems.find((item) => item._id === selectedItem._id)) {
            allItems.unshift(selectedItem);
        }

        const filteredItems = UsedInTemplates ? allItems.filter((item) => (item.locationCount ?? 0) > 0) : allItems;

        return filteredItems.map((item) => ({
            label: createItemLabel(item),
            value: item._id,
        }));
    }, [microstoreItemsData, selectedItem, UsedInTemplates]);

    const fetchOptions = React.useCallback(
        async (query: string): Promise<Option<string>[]> => {
            const result = await refetch({
                offset: 0,
                limit: 30,
                sortBy: 'name',
                sortOrder: 'asc',
                filters: { searchValue: query || undefined },
            });

            const queryItems = result.data?.microstoreItems?.items || [];
            const filteredItems = UsedInTemplates
                ? queryItems.filter((item) => (item.locationCount ?? 0) > 0)
                : queryItems;

            return filteredItems.map((item) => ({
                label: createItemLabel(item),
                value: item._id,
            }));
        },
        [refetch, UsedInTemplates],
    );

    const handleChange = (itemId: string | null) => {
        const allItems = [
            ...(microstoreItemsData?.microstoreItems?.items || []),
            ...(selectedItem ? [selectedItem] : []),
        ];
        const item = allItems.find((item) => item._id === itemId) ?? null;

        setSelectedItem(item);
        onChange(item);
    };

    return (
        <TotemSelect
            dataTest={dataTest}
            label={label}
            placeholder={placeholder}
            value={value}
            options={defaultOptions}
            onChange={handleChange}
            fetchOptions={fetchOptions}
            defaultOptions={defaultOptions}
            isClearable={isClearable}
        />
    );
};
